import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { useInterval } from 'react-use';
import _ from 'lodash';

import {
  cancelPendingAITaggingRequests,
  pollAITaggingRequests,
  tagEntriesWithAI,
} from 'src/api/api';
import { useCatalogPageContext } from 'src/components/Insights/Catalog/CatalogPageProvider';
import { SuggestCodesDialog } from 'src/components/Insights/Dialogs/SuggestCodesDialog';
import { refreshEntries } from 'src/redux/catalog/catalog-slice';
import {
  AIRequest,
  AIRequestStatus,
  AITaggingResponse,
  Entry,
} from 'src/types/insights';
import { useLocalStorage } from 'src/util/hooks';
import { createProvider } from 'src/util/provider';

export const [AIProvider, useAIContext] = createProvider(() => {
  const { catalog, allEntries, showModal, hideModal, dispatch } =
    useCatalogPageContext();
  const [aiSuggestionIntroEnabled, setAiSuggestionIntroEnabled] =
    useLocalStorage('aiSuggestionIntroEnabled', true);
  const [aiOn, setAIOn] = useState(true);

  // we keep track of tagRequests as a kind of "session" since the server always returns
  // all the requests ever made by the current user. this way, we can show the status of
  // the current session of requests only.
  const [tagRequests, setTagRequests] = useState<AIRequest[]>([]);
  const [aiTaggingResponse, setAiTaggingResponse] = useState<AITaggingResponse>(
    {
      pending_ai_requests: [],
      completed_ai_requests: [],
      failed_ai_requests: [],
      canceled_ai_requests: [],
    }
  );

  const annotationIds = useMemo(
    () => tagRequests.map((tr) => tr.annotation_id),
    [tagRequests]
  );

  const filterAIRequests = useCallback(
    (requests?: AIRequest[]) =>
      (requests ?? []).filter((tr) =>
        tagRequests?.some((pr) => pr.annotation_id === tr.annotation_id)
      ),
    [tagRequests]
  );

  const pendingTagRequests = useMemo(
    () => filterAIRequests(aiTaggingResponse.pending_ai_requests),
    [filterAIRequests, aiTaggingResponse]
  );

  const succeededTagRequests = useMemo(
    () =>
      filterAIRequests(aiTaggingResponse.completed_ai_requests).filter(
        (r) => r.ai_request_code_count
      ),
    [filterAIRequests, aiTaggingResponse]
  );

  const emptyTagRequests = useMemo(
    () =>
      filterAIRequests(aiTaggingResponse.completed_ai_requests).filter(
        (r) => !r.ai_request_code_count
      ),
    [filterAIRequests, aiTaggingResponse]
  );

  const failedTagRequests = useMemo(() => {
    return filterAIRequests(aiTaggingResponse.failed_ai_requests);
  }, [filterAIRequests, aiTaggingResponse]);

  const canceledTagRequests = useMemo(
    () => filterAIRequests(aiTaggingResponse.canceled_ai_requests),
    [filterAIRequests, aiTaggingResponse]
  );

  const clearTagRequests = () => {
    setTagRequests([]);
    setAiTaggingResponse({
      pending_ai_requests: [],
      completed_ai_requests: [],
      failed_ai_requests: [],
      canceled_ai_requests: [],
    });
  };

  const succeededCount = succeededTagRequests?.length ?? 0;
  const failedCount = failedTagRequests?.length ?? 0;
  const canceledCount = canceledTagRequests?.length ?? 0;
  const emptyCount = emptyTagRequests?.length ?? 0;
  const unsuccessfulCount = emptyCount + failedCount + canceledCount;
  const completedCount =
    succeededCount + failedCount + canceledCount + emptyCount;
  const totalCount = tagRequests?.length ?? 0;
  const pendingCount =
    pendingTagRequests?.length || totalCount - completedCount;

  const taggingProgress = useMemo(
    () => ({
      // at least 1% so that progress bar appears
      percent: totalCount
        ? Math.max(1, Math.ceil((completedCount / totalCount) * 100))
        : 1,
      pendingPercent: totalCount
        ? Math.ceil(pendingCount / totalCount) * 100
        : 1,
      failedPercent: totalCount
        ? Math.ceil(unsuccessfulCount / totalCount) * 100
        : 1,
      pendingCount,
      succeededCount,
      failedCount,
      canceledCount,
      emptyCount,
      unsuccessfulCount,
      completedCount,
      totalCount,
    }),
    [
      totalCount,
      pendingCount,
      completedCount,
      succeededCount,
      failedCount,
      canceledCount,
      emptyCount,
      unsuccessfulCount,
    ]
  );

  // ••• ACTIONS

  const tagWithAI = async (
    entryIds: Entry['id'][],
    onTaggingStart?: () => void
  ) => {
    const start = () => {
      startTaggingWithAI(entryIds);
      onTaggingStart?.();
    };

    if (aiSuggestionIntroEnabled) {
      const onConfirm = () => {
        hideModal();
        start();
      };
      showModal(<SuggestCodesDialog {...{ onConfirm }} />);
    } else {
      start();
    }
  };

  const startTaggingWithAI = async (entryIds: Entry['id'][]) => {
    const hasRequestsInProgress = !!tagRequests.length;
    const newAnnotationIds = _.compact(
      entryIds.map((id) => allEntries.find((e) => e.id === id)?.annotation_id)
    );
    const newPendingRequests = newAnnotationIds.map(toPendingAIRequest);

    // this optimistically updates the ui
    setTagRequests(
      _.uniqBy([...tagRequests, ...newPendingRequests], 'annotation_id')
    );

    try {
      const response = await tagEntriesWithAI(catalog.id, newAnnotationIds);
      if (!hasRequestsInProgress) setAiTaggingResponse(response);
    } catch (e) {
      onFailure('Failed to start AI tagging.');
    }
  };

  const poll = useCallback(
    async (withRefresh = true) => {
      try {
        const response = await pollAITaggingRequests(catalog.id, annotationIds);
        setAiTaggingResponse(response);

        const completedAnnotationIds = tagRequests
          .filter((r) =>
            response.completed_ai_requests?.some(
              (cr) => cr.annotation_id === r.annotation_id
            )
          )
          .map((r) => r.annotation_id);

        if (withRefresh && completedAnnotationIds.length) {
          dispatch(
            refreshEntries({
              annotation_ids: completedAnnotationIds,
              catalogId: catalog.id,
            })
          );
        }
      } catch (e) {
        onFailure('Failed to poll AI tagging requests.');
      }
    },
    [catalog.id, annotationIds, tagRequests, dispatch, setAiTaggingResponse]
  );

  const stopTaggingWithAI = async () => {
    try {
      setAiTaggingResponse(await cancelPendingAITaggingRequests(catalog.id));
    } catch (e) {
      onFailure('Failed to stop AI tagging.');
    }
  };

  const onFailure = (message: string) => {
    toast.error(message, { position: toast.POSITION.BOTTOM_RIGHT });
  };

  // ••• EFFECTS

  useInterval(poll, pendingCount ? 3000 : null);
  useEffect(() => {
    poll(false);
  }, []); // check for requests on startup

  // ••• CONTEXT

  return {
    tagWithAI,
    stopTaggingWithAI,
    clearTagRequests,

    aiOn,
    setAIOn,

    pendingTagRequests,
    succeededTagRequests,
    emptyTagRequests,
    failedTagRequests,
    canceledTagRequests,
    taggingProgress,

    aiSuggestionIntroEnabled,
    setAiSuggestionIntroEnabled,
  };
});

// HELPERS

function toPendingAIRequest(annotationId: Entry['annotation_id']): AIRequest {
  return {
    annotation_id: annotationId,
    ai_request_status: AIRequestStatus.pending,
  };
}
